import { useTranslation } from "react-i18next";
import React, { useMemo, useState, Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { useNavigate } from "react-router-dom";
import StatusBadge from "../../../../components/StatusBadge";
import Loading from "../../../../components/Loading";
import { useQuery } from "react-query";
import { apollo } from "../../../../utils/apollo";
import {
    GET_SHAFAFIYA_POLICY_MEMBERS,
    CANCEL_SHAFAFIYA_MEMBER,
    RECHECK_COC_STATUS,
} from "../../../../utils/graphql";
import { toast } from "react-toastify";
import InformationColumn from "../../../../components/InformationColumn";
import CocHistoryView from "./coc-history-view";
import Button from "../../../../components/Button";
import {
    CalendarIcon,
    FingerPrintIcon,
    HashtagIcon,
    ShieldExclamationIcon,
    ArrowPathIcon,
    ClockIcon,
    NoSymbolIcon,
    ChartBarIcon,
} from "@heroicons/react/24/outline";
import IconButtonLink from "../../../../components/IconButtonLink";

function ShafafiyaPolicyView({ loading, policy }) {
    const { t } = useTranslation();
    if (loading || !policy) return <Loading />;
    return (
        <div className="mt-6 border-t border-slate-100">
            <div className="space-y-8">
                {/* Policy Details Section */}
                <div className="pt-8">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        <InformationColumn
                            title={t("Policy Uuid")}
                            value={policy?.policyUuid}
                        />
                        <InformationColumn
                            title={t("Shafafiya Ref No")}
                            value={policy?.policyNumber}
                        />
                        <InformationColumn
                            title={t("Total Penalty")}
                            value={
                                policy?.policiable?.totalPenalty != null
                                    ? `${policy?.policiable?.totalPenalty} ${t("AED")}`
                                    : null
                            }
                        />
                        <InformationColumn
                            title={t("Validity Date")}
                            value={policy?.policiable?.validityDate}
                        />
                        <InformationColumn
                            title={t("Policy Type")}
                            value={policy?.policiable?.shafafiyaPolicyType}
                        />
                        <InformationColumn
                            title={t("Integration Status")}
                            value={
                                <StatusBadge
                                    status={policy?.policiable?.shafafiyaIntegrationStatus}
                                />
                            }
                        />
                    </div>
                </div>

                <ShafafiyaPolicyMembers policy={policy} key={policy?.policyUuid} />
            </div>
        </div>
    );
}

function ShafafiyaPolicyMembers({ policy }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [historyEid, setHistoryEid] = useState(null);
    const [cancelMemberRef, setCancelMemberRef] = useState(null);
    const [canceling, setCanceling] = useState(false);

    const handleRecheckMember = async (eid) => {
        if (!eid) return;
        try {
            toast.info(t("Initiating status re-sync..."));
            const { data } = await apollo.mutate({
                mutation: RECHECK_COC_STATUS,
                variables: {
                    eid,
                },
            });
            if (data?.reCheckCocStatus?.success) {
                toast.success(t("Member status updated successfully."));
                
                const newPolicyUuid = data?.reCheckCocStatus?.policy?.policyUuid;
                if (newPolicyUuid && newPolicyUuid !== policy.policyUuid) {
                    navigate(`/policy/view/${newPolicyUuid}`);
                } else {
                    fetchShafafiyaPolicyMembers.refetch();
                }
            } else {
                toast.error(t("Failed to update member status."));
            }
        } catch (e) {
            toast.error(e.message || t("Failed to re-sync member"));
        }
    };

    const handleCancelMember = async () => {
        if (!cancelMemberRef) return;
        setCanceling(true);
        try {
            const { data } = await apollo.mutate({
                mutation: CANCEL_SHAFAFIYA_MEMBER,
                variables: {
                    referenceNo: cancelMemberRef,
                },
            });
            if (data?.cancelShafafiyaMember) {
                toast.success(data?.cancelShafafiyaMember);
                fetchShafafiyaPolicyMembers.refetch();
            } else {
                toast.error(t("Failed to cancel membership certificate."));
            }
        } catch (e) {
            toast.error(e.message || t("Failed to cancel member"));
        } finally {
            setCanceling(false);
            setCancelMemberRef(null);
        }
    };

    const fetchShafafiyaPolicyMembers = useQuery({
        queryKey: ["shafafiya_policy_member_list", policy.policyUuid],
        queryFn: async () => {
            const { data } = await apollo.query({
                query: GET_SHAFAFIYA_POLICY_MEMBERS,
                variables: {
                    policyUuid: policy.policyUuid,
                },
            });
            return data?.getShafafiyaPolicyMembers;
        },
        keepPreviousData: false,
    });

    const members = useMemo(
        () =>
            fetchShafafiyaPolicyMembers?.isSuccess &&
                !fetchShafafiyaPolicyMembers?.isLoading
                ? fetchShafafiyaPolicyMembers?.data
                : null,
        [fetchShafafiyaPolicyMembers],
    );

    if (fetchShafafiyaPolicyMembers.isLoading)
        return (
            <div className="py-12">
                <Loading message="Loading members..." />
            </div>
        );

    if (!members?.length) return null;

    return (
        <div className="mt-12">
            <div className="flex items-center gap-3 mb-6">
                <div className="h-8 w-1.5 bg-primary-500 rounded-full shadow-[0_0_10px_rgba(59,130,246,0.5)]" />
                <h3 className="text-xl font-black text-slate-900 uppercase tracking-tight">
                    {t("Policy Members")}
                </h3>
                <span className="text-xs font-bold text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full ms-2">
                    {members.length}
                    {t("Total")}
                </span>
            </div>

            <div className="flow-root">
                <div className="overflow-auto pb-16 rounded-2xl border border-slate-200/60 bg-white shadow-sm">
                    <div className="inline-block min-w-full align-middle">
                        <table className="min-w-full divide-y divide-slate-200">
                            <thead className="bg-slate-50/50">
                                <tr>
                                    <th
                                        scope="col"
                                        className="px-6 py-4 text-start text-[11px] font-black uppercase tracking-wider text-slate-500"
                                    >
                                        {t("Member Identity")}
                                    </th>
                                    <th
                                        scope="col"
                                        className="px-6 py-4 text-start text-[11px] font-black uppercase tracking-wider text-slate-500"
                                    >
                                        {t("Timing & Reference")}
                                    </th>
                                    <th
                                        scope="col"
                                        className="px-6 py-4 text-start text-[11px] font-black uppercase tracking-wider text-slate-500"
                                    >
                                        {t("Financial Info")}
                                    </th>
                                    <th
                                        scope="col"
                                        className="px-6 py-4 text-start text-[11px] font-black uppercase tracking-wider text-slate-500"
                                    >
                                        {t("Gap Analysis")}
                                    </th>
                                    <th
                                        scope="col"
                                        className="px-6 py-4 text-start text-[11px] font-black uppercase tracking-wider text-slate-500"
                                    >
                                        {t("System Status")}
                                    </th>
                                    <th scope="col" className="relative py-4 ps-3 pe-6">
                                        <span className="sr-only">{t("Quick Actions")}</span>
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 bg-white">
                                {members.map((member) => (
                                    <tr
                                        key={member.id}
                                        className="hover:bg-slate-50/50 transition-colors group"
                                    >
                                        {/* Identity: 3-line stack */}
                                        <td className="whitespace-nowrap px-6 py-6 align-top">
                                            <div className="flex flex-col gap-2">
                                                <div
                                                    className="gap-1.5"
                                                    title={t("Emirates ID")}
                                                >
                                                    <div className="flex items-center gap-1.5">
                                                        <FingerPrintIcon className="h-3.5 w-3.5 text-slate-400" />
                                                        <span className="text-[10px] font-black uppercase tracking-widest text-slate-500 w-8">
                                                            {t("EID")}
                                                        </span>
                                                    </div>
                                                    <div className="text-sm font-bold text-slate-900 font-mono tracking-tight">
                                                        {member.eid}
                                                    </div>
                                                </div>
                                                <div
                                                    className="gap-1.5 opacity-60"
                                                    title={t("Unified Number")}
                                                >
                                                    <div className="flex items-center">
                                                        <HashtagIcon className="h-3 w-3 text-slate-400" />
                                                        <span className="text-[10px] font-black uppercase tracking-widest text-slate-500 w-8">
                                                            {t("UID")}
                                                        </span>
                                                    </div>
                                                    <div className="text-sm font-bold text-slate-900 font-mono tracking-tight">
                                                        {member.uid}
                                                    </div>
                                                </div>
                                                <div className="h-4" /> {/* Spacer for 3rd line */}
                                            </div>
                                        </td>

                                        {/* Reference & Timing: 3-line stack */}
                                        <td className="whitespace-nowrap px-6 py-6 align-top">
                                            <div className="flex flex-col gap-2">
                                                <div className="flex flex-col">
                                                    <span className="text-[9px] font-black uppercase tracking-widest text-slate-400">
                                                        {t("Reference No")}
                                                    </span>
                                                    <span className="text-xs font-bold text-slate-700 font-mono">
                                                        {member.shafafiyaReferenceNo}
                                                    </span>
                                                </div>
                                                <div className="flex items-center gap-1.5 text-slate-400">
                                                    <CalendarIcon className="h-3 w-3" />
                                                    <span className="text-[10px] font-black uppercase tracking-widest">
                                                        {t("Submitted")}
                                                    </span>
                                                </div>
                                                <div className="text-[11px] font-bold text-slate-500">
                                                    {member.createdAt}
                                                </div>
                                            </div>
                                        </td>

                                        {/* Financial Info: 3-line stack */}
                                        <td className="whitespace-nowrap px-6 py-6 align-top">
                                            <div className="flex flex-col gap-1">
                                                <div className="flex items-baseline gap-1">
                                                    <span className="text-lg font-black text-rose-600 tracking-tight leading-none">
                                                        {member.penalty}
                                                    </span>
                                                    <span className="text-[10px] font-black text-rose-500">
                                                        {t("AED")}
                                                    </span>
                                                </div>
                                                <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                                                    {t("Accrued Penalty")}
                                                </span>
                                                <div className="h-4" /> {/* Spacer for alignment */}
                                            </div>
                                        </td>

                                        {/* Gap Analysis: 3-line stack */}
                                        <td className="whitespace-nowrap px-6 py-6 align-top">
                                            <div className="flex flex-col gap-2">
                                                <div className="flex items-center gap-2 bg-amber-50 px-2 py-1.5 rounded-lg border border-amber-100/50 w-fit">
                                                    <ShieldExclamationIcon className="h-3.5 w-3.5 text-amber-500" />
                                                    <span className="text-[11px] font-bold text-amber-700 leading-none">
                                                        {member.nonInsuranceDays}
                                                        {t("Days")}
                                                    </span>
                                                </div>
                                                <span className="text-[9px] font-black uppercase tracking-widest text-slate-400">
                                                    {t("Insurance Gap")}
                                                </span>
                                                <div className="h-4" /> {/* Spacer */}
                                            </div>
                                        </td>

                                        {/* System Status: 3-line stack */}
                                        <td className="px-6 py-6 align-top">
                                            <div className="flex flex-col gap-2">
                                                <StatusBadge status={member.integrationStatus} />
                                                <div className="max-w-[180px]">
                                                    {member.errorCode ? (
                                                        <div className="flex items-start gap-1.5">
                                                            <div className="h-1.5 w-1.5 rounded-full bg-rose-500 mt-1.5 shrink-0 animate-pulse" />
                                                            <p
                                                                className="text-[11px] text-rose-600 font-medium leading-tight line-clamp-2"
                                                                title={member.errorCode}
                                                            >
                                                                {member.errorCode}
                                                            </p>
                                                        </div>
                                                    ) : (
                                                        <span className="text-[10px] font-black uppercase tracking-widest text-slate-300 italic">
                                                            {t("No flags reported")}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </td>

                                        {/* Actions: Vertical 3-line stack */}
                                        <td className="relative whitespace-nowrap px-6 py-6 align-top">
                                            <div className="flex flex-col gap-2">
                                                <button
                                                    onClick={() => setHistoryEid(member.eid)}
                                                    className="flex items-center justify-start gap-2 px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-primary-600 hover:text-white hover:bg-primary-600 bg-primary-50/50 rounded-lg transition-all active:scale-[0.95] border border-primary-100/50"
                                                >
                                                    <ClockIcon className="h-3.5 w-3.5" />
                                                    <span>{t("History")}</span>
                                                </button>
                                                <button
                                                    onClick={() => handleRecheckMember(member.eid)}
                                                    className="flex items-center justify-start gap-2 px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-indigo-600 hover:text-white hover:bg-indigo-600 bg-indigo-50/50 rounded-lg transition-all active:scale-[0.95] border border-indigo-100/50"
                                                >
                                                    <ArrowPathIcon className="h-3.5 w-3.5" />
                                                    <span>{t("Re-sync")}</span>
                                                </button>
                                                <button
                                                    onClick={() =>
                                                        setCancelMemberRef(member.shafafiyaReferenceNo)
                                                    }
                                                    className="flex items-center justify-start gap-2 px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-rose-600 hover:text-white hover:bg-rose-600 bg-rose-50/50 rounded-lg transition-all active:scale-[0.95] border border-rose-100/50"
                                                >
                                                    <NoSymbolIcon className="h-3.5 w-3.5" />
                                                    <span>{t("Cancel")}</span>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Modal for viewing history */}
            <Transition.Root show={!!historyEid} as={Fragment}>
                <Dialog
                    as="div"
                    className="relative z-50"
                    onClose={() => setHistoryEid(null)}
                >
                    <Transition.Child
                        as={Fragment}
                        enter="transition-opacity ease-linear duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="transition-opacity ease-linear duration-300"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm" />
                    </Transition.Child>

                    <div className="fixed inset-0 overflow-y-auto">
                        <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-6">
                            <Transition.Child
                                as={Fragment}
                                enter="transition ease-in-out duration-300 transform"
                                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                                enterTo="opacity-100 translate-y-0 sm:scale-100"
                                leave="transition ease-in-out duration-200 transform"
                                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            >
                                <Dialog.Panel className="relative w-full max-w-7xl transform overflow-hidden text-start transition-all">
                                    {historyEid && (
                                        <CocHistoryView
                                            emiratesId={historyEid}
                                            onClose={() => setHistoryEid(null)}
                                        />
                                    )}
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition.Root>

            {/* Cancel Confirmation Modal */}
            <Transition.Root show={!!cancelMemberRef} as={Fragment}>
                <Dialog
                    as="div"
                    className="relative z-[70]"
                    onClose={(val) => {
                        if (!canceling) setCancelMemberRef(null);
                    }}
                >
                    <Transition.Child
                        as={Fragment}
                        enter="transition-opacity ease-linear duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="transition-opacity ease-linear duration-300"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm" />
                    </Transition.Child>
                    <div className="fixed inset-0 overflow-y-auto">
                        <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-6">
                            <Transition.Child
                                as={Fragment}
                                enter="transition ease-in-out duration-300 transform"
                                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                                enterTo="opacity-100 translate-y-0 sm:scale-100"
                                leave="transition ease-in-out duration-200 transform"
                                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            >
                                <Dialog.Panel className="relative w-full max-w-md transform overflow-hidden rounded-2xl bg-white text-start shadow-2xl transition-all border border-slate-200">
                                    <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-3">
                                        <div className="h-6 w-1 bg-rose-500 rounded-full" />
                                        <Dialog.Title className="text-sm font-black uppercase tracking-widest text-slate-900">
                                            {t("Cancel Certificate")}
                                        </Dialog.Title>
                                    </div>
                                    <div className="p-6">
                                        <p className="text-sm text-slate-600 leading-relaxed">
                                            {t(
                                                "Are you sure you want to cancel the continuity certificate for reference No.",
                                            )}
                                            <span className="font-bold text-slate-900 font-mono px-1">
                                                {cancelMemberRef}
                                            </span>
                                            <span className="rtl:hidden">?</span>
                                            <span className="ltr:hidden">؟</span>
                                        </p>
                                        <div className="mt-4 rounded-xl bg-amber-50 p-4 border border-amber-100 shadow-sm">
                                            <div className="flex">
                                                <div className="ms-1">
                                                    <h3 className="text-[10px] font-black uppercase tracking-widest text-amber-800 mb-1">
                                                        {t("Warning")}
                                                    </h3>
                                                    <div className="text-sm text-amber-700 leading-relaxed">
                                                        <p className="gap-1">
                                                            {t("If you cancel this,")}
                                                            {' '}
                                                            <strong className="font-bold">{t("ALL")}</strong>
                                                            {' '}
                                                            {t(
                                                                "members under this reference will be canceled in the Shafafiya system.",
                                                            )}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <p className="mt-4 text-[10px] font-black uppercase tracking-widest text-rose-500">
                                            {t("This action cannot be undone.")}
                                        </p>
                                        <div className="mt-8 flex items-center justify-end gap-3">
                                            <Button
                                                secondary
                                                onClick={() => setCancelMemberRef(null)}
                                                disabled={canceling}
                                                className="!px-6"
                                            >
                                                {t("Keep Member")}
                                            </Button>
                                            <Button
                                                onClick={handleCancelMember}
                                                disabled={canceling}
                                                danger
                                                className="!px-6 shadow-lg shadow-rose-500/20 transition-all active:scale-[0.97]"
                                            >
                                                {canceling ? t("Processing...") : t("Cancel Member")}
                                            </Button>
                                        </div>
                                    </div>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition.Root>
        </div>
    );
}

export default ShafafiyaPolicyView;
